import { useEffect, useState, useMemo } from "react";
import { Clock, Flame, Zap, Hourglass } from "lucide-react";

/**
 * Contador en vivo para subastas BOE.
 *
 * Estados visuales (en orden de urgencia):
 *  - Terminada (>= fecha_fin) → texto gris "Subasta terminada".
 *  - Crítico (< 24h)          → rojo + pulse + segundos.
 *  - Urgente (1-7 días)       → naranja, días + horas.
 *  - Normal (> 7 días)        → marrón, días.
 *
 * Variantes:
 *  - `variant="hero"`: tamaño grande, para SubastaDetail.
 *  - `variant="badge"`: compacto, para cards en listados (más sutil pero llamativo).
 */
export default function Countdown({ fechaFin, variant = "hero" }) {
  // Tick interval: si quedan < 24h, refrescar cada segundo. Si más, cada minuto.
  const target = useMemo(() => {
    if (!fechaFin) return null;
    try {
      return new Date(fechaFin).getTime();
    } catch {
      return null;
    }
  }, [fechaFin]);

  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!target) return undefined;
    const diff = target - Date.now();
    if (diff <= 0) return undefined;
    // Si quedan más de 24h, refrescamos cada minuto. Si menos, cada segundo.
    const tickMs = diff < 24 * 3600 * 1000 ? 1000 : 60_000;
    const id = setInterval(() => setNow(Date.now()), tickMs);
    return () => clearInterval(id);
  }, [target, now]);

  if (!target) return null;
  const diff = target - now;

  if (diff <= 0) {
    if (variant === "badge") return null;
    return (
      <div className="inline-flex items-center gap-2 text-stone-400 text-xs uppercase tracking-wider" data-testid="countdown-ended">
        <Clock className="w-3.5 h-3.5" /> Subasta terminada
      </div>
    );
  }

  const totalSec = Math.floor(diff / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;

  // Determinar urgencia
  let urgency = "normal";       // > 7 días
  if (diff < 86_400_000) urgency = "crit";    // < 1 día
  else if (diff < 7 * 86_400_000) urgency = "urgent"; // 1-7 días

  if (variant === "badge") {
    // Compacto: 1 línea para cards en listados.
    const cfg = {
      normal: { color: "bg-stone-800 text-stone-300 border-stone-700", Icon: Clock, label: `${days}d ${hours}h` },
      urgent: { color: "bg-orange-950/60 text-orange-300 border-orange-800/60", Icon: Zap, label: days > 0 ? `${days}d ${hours}h` : `${hours}h ${mins}m` },
      crit:   { color: "bg-red-950/70 text-red-300 border-red-800/70 animate-pulse", Icon: Flame, label: hours > 0 ? `${hours}h ${mins}m` : `${mins}m ${secs}s` },
    }[urgency];
    return (
      <div
        className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider border px-2 py-1 rounded-sm ${cfg.color}`}
        data-testid={`countdown-badge-${urgency}`}
        title={`Termina en ${days}d ${hours}h ${mins}m`}
      >
        <cfg.Icon className="w-3 h-3" />
        <span>{cfg.label}</span>
      </div>
    );
  }

  // Variant "hero": destacado en la ficha
  const styles = {
    normal: {
      box: "bg-stone-900/70 border-stone-700",
      label: "text-stone-400",
      time:  "text-stone-100",
      Icon:  Hourglass,
      headline: "Cuenta atrás",
    },
    urgent: {
      box: "bg-orange-950/40 border-orange-700/50",
      label: "text-orange-300",
      time:  "text-orange-100",
      Icon:  Zap,
      headline: "Termina pronto",
    },
    crit: {
      box: "bg-red-950/50 border-red-700/60 animate-pulse",
      label: "text-red-300",
      time:  "text-red-50",
      Icon:  Flame,
      headline: "¡Últimas horas!",
    },
  }[urgency];

  const Block = ({ value, unit }) => (
    <div className="flex flex-col items-center min-w-[44px] sm:min-w-[56px]">
      <span className={`font-display font-black tabular-nums text-2xl sm:text-3xl lg:text-4xl leading-none ${styles.time}`} data-testid={`countdown-${unit}`}>
        {String(value).padStart(2, "0")}
      </span>
      <span className={`text-[10px] uppercase tracking-widest mt-1.5 ${styles.label}`}>{unit}</span>
    </div>
  );

  return (
    <div
      className={`flex items-center gap-3 sm:gap-5 border rounded-sm px-4 py-3.5 ${styles.box}`}
      data-testid={`countdown-hero-${urgency}`}
    >
      <div className="flex items-center gap-2">
        <styles.Icon className={`w-5 h-5 ${styles.label}`} />
        <span className={`text-[10px] uppercase tracking-[0.3em] font-bold ${styles.label} hidden sm:inline`}>
          {styles.headline}
        </span>
      </div>
      <div className="flex items-baseline gap-2 sm:gap-3 lg:gap-4">
        <Block value={days} unit="días" />
        <span className={`font-display font-black text-2xl sm:text-3xl ${styles.time} opacity-30`}>:</span>
        <Block value={hours} unit="horas" />
        <span className={`font-display font-black text-2xl sm:text-3xl ${styles.time} opacity-30`}>:</span>
        <Block value={mins} unit="min" />
        {urgency === "crit" && (
          <>
            <span className={`font-display font-black text-2xl sm:text-3xl ${styles.time} opacity-30`}>:</span>
            <Block value={secs} unit="seg" />
          </>
        )}
      </div>
    </div>
  );
}
